import { useMemo } from 'react';

import { useSelector } from '../providers/StoreProvider';
import { type Bookmaker, type Offers } from '../types';
import { oddsShownType } from '../types';
import { type BookmakerData, emptyBookmakerBranding, getOcBookmakerBranding } from '../utils';

import { useBookmakersAll } from './useBookmakersAll';

interface UseBookmakerDataReturn {
  bookmaker: Bookmaker;
  bookmakerData: BookmakerData;
}

const defaultBookmaker: Bookmaker = {
  brandColour: '',
  code: '',
  logoLarge: '',
  logoSmall: '',
  name: '',
  offers: { signupOffers: [], specialOffers: [] } as Offers,
  priority: 0,
  textColour: '',
  type: '',
};

export const useBookmakerData = (bookieCode: string): UseBookmakerDataReturn => {
  const { oddsShown, resourcesUrl } = useSelector(state => ({
    oddsShown: state.oddsShown,
    resourcesUrl: state.resourcesUrl,
  }));
  const allBookmakers = useBookmakersAll();

  return useMemo(() => {
    const bookmaker = allBookmakers.find(bookmaker => bookmaker.code === bookieCode) || defaultBookmaker;

    // Unsupported regions render non-clickable OddsChecker odds, so swap in OC branding.
    if (oddsShown === oddsShownType.OC_NON_CLICKABLE) {
      return { bookmaker, bookmakerData: getOcBookmakerBranding(resourcesUrl) };
    }

    const bookmakerData: BookmakerData = bookmaker.code
      ? {
          brandColour: bookmaker.brandColour,
          logoLarge: bookmaker.logoLarge,
          logoSmall: bookmaker.logoSmall,
          textColour: bookmaker.textColour,
        }
      : emptyBookmakerBranding;

    return {
      bookmaker,
      bookmakerData,
    };
  }, [bookieCode, allBookmakers, oddsShown, resourcesUrl]);
};
